/**
 * The kitchen game: XP, levels, a weekly cooking streak, badges, and the buddy
 * who cheers you on.
 *
 * Imported by BOTH halves. Nothing here is stored: every number is recomputed
 * from rows that record things that actually happened (a recipe saved, a run
 * cooked), the way Stash and Adulting do it. So a rule added later applies to
 * history too, and there is no XP column to drift out of step with reality.
 *
 * What it rewards is the point of the app — getting a saved recipe MADE.
 * Saving is worth a little; cooking is worth a lot; cooking something that had
 * been waiting, or cooking it on the day you planned, is worth more again.
 */

export const XP = {
  save: 10,
  cook: 40,
  /** Extra, the first time a recipe is ever made. */
  newDish: 30,
  /** Extra, when it was cooked on the day it was planned for. */
  onPlan: 25,
  /** Extra, when it had sat for a week or more before its first make. */
  rescued: 25,
} as const

export const LEVELS = [
  { min: 0, title: 'Taster' },
  { min: 150, title: 'Prep Cook' },
  { min: 400, title: 'Line Cook' },
  { min: 900, title: 'Sous Chef' },
  { min: 1800, title: 'Chef' },
  { min: 3500, title: 'Head Chef' },
  { min: 7000, title: 'Kitchen Legend' },
] as const

export interface Level {
  index: number
  title: string
  floor: number
  next: number | null
  nextTitle: string | null
  /** 0–1 through the current level. */
  progress: number
}

export function levelFor(xp: number): Level {
  let index = 0
  for (let i = 0; i < LEVELS.length; i++) if (xp >= LEVELS[i]!.min) index = i
  const cur = LEVELS[index]!
  const nxt = LEVELS[index + 1]
  const progress = nxt ? (xp - cur.min) / (nxt.min - cur.min) : 1
  return {
    index, title: cur.title, floor: cur.min,
    next: nxt ? nxt.min : null, nextTitle: nxt ? nxt.title : null,
    progress: Math.max(0, Math.min(1, progress)),
  }
}

/* ── Facts ──────────────────────────────────────────────────────────────── */

/** The counts everything else is derived from. The server fills these in from rows. */
export interface KitchenFacts {
  /** Real recipes saved — samples do not count. */
  saved: number
  /** Completed cooks, session or recorded. */
  cooks: number
  /** Different recipes ever made. */
  dishes: number
  /** Cooks that landed on the recipe's planned day. */
  onPlan: number
  /** Recipes first made after waiting a week or more. */
  rescued: number
  /** `YYYY-MM-DD` business dates with a cook, any order, duplicates fine. */
  cookedOn: string[]
}

export function xpFor(f: KitchenFacts): number {
  return f.saved * XP.save + f.cooks * XP.cook + f.dishes * XP.newDish + f.onPlan * XP.onPlan + f.rescued * XP.rescued
}

/* ── The weekly streak ──────────────────────────────────────────────────── */

/**
 * Weeks, not days. Nobody cooks a saved recipe every day, and a daily streak
 * would break every week for a normal person — which teaches them to ignore
 * it. A week with at least one cook keeps it alive.
 *
 * Weeks start on Monday. 1970-01-01 was a Thursday, so +3 lines day 0 up with
 * the Monday before it.
 */
export function weekOf(date: string): number {
  const days = Math.floor(Date.parse(`${date}T12:00:00Z`) / 86_400_000)
  return Math.floor((days + 3) / 7)
}

export interface Streak {
  /** Consecutive weeks with a cook, ending this week or last week. */
  current: number
  best: number
  /** Whether this week already counts. False means "cook this week to keep it". */
  thisWeek: boolean
}

export function streakFor(cookedOn: string[], today: string): Streak {
  const weeks = [...new Set(cookedOn.map(weekOf))].sort((a, b) => a - b)
  let best = 0
  let run = 0
  for (let i = 0; i < weeks.length; i++) {
    run = i > 0 && weeks[i] === weeks[i - 1]! + 1 ? run + 1 : 1
    best = Math.max(best, run)
  }
  const now = weekOf(today)
  const have = new Set(weeks)
  /*
   * The current streak may end LAST week. On a Monday morning nobody has cooked
   * yet this week, and a streak that reads 0 until they do punishes the very
   * start of the week it is meant to encourage. Same rule as Stash's daily one.
   */
  let end = have.has(now) ? now : have.has(now - 1) ? now - 1 : null
  let current = 0
  while (end !== null && have.has(end)) { current += 1; end -= 1 }
  return { current, best, thisWeek: have.has(now) }
}

/* ── Badges ─────────────────────────────────────────────────────────────── */

export interface BadgeFacts extends KitchenFacts { bestStreak: number }

export interface Badge {
  key: string
  /** A name from the Icon set — never an emoji (rule 13). */
  icon: 'bookmark' | 'book' | 'flame' | 'bowl' | 'chef' | 'globe' | 'calendar' | 'clock' | 'trophy' | 'star' | 'heart'
  title: string
  hint: string
  earned: (f: BadgeFacts) => boolean
}

export const BADGES: readonly Badge[] = [
  { key: 'first_save', icon: 'bookmark', title: 'First save', hint: 'Save a recipe', earned: (f) => f.saved >= 1 },
  { key: 'first_cook', icon: 'flame', title: 'First cook', hint: 'Make something you saved', earned: (f) => f.cooks >= 1 },
  { key: 'right_on_time', icon: 'calendar', title: 'Right on time', hint: 'Cook something on the day you planned it', earned: (f) => f.onPlan >= 1 },
  { key: 'rescuer', icon: 'clock', title: 'Rescuer', hint: 'Make a recipe that waited a week or more', earned: (f) => f.rescued >= 1 },
  { key: 'regular', icon: 'bowl', title: 'Regular', hint: 'Cook 10 times', earned: (f) => f.cooks >= 10 },
  { key: 'collector', icon: 'book', title: 'Collector', hint: 'Save 25 recipes', earned: (f) => f.saved >= 25 },
  { key: 'explorer', icon: 'globe', title: 'Explorer', hint: 'Make 10 different dishes', earned: (f) => f.dishes >= 10 },
  { key: 'planner', icon: 'star', title: 'Planner', hint: 'Cook 5 recipes on their planned day', earned: (f) => f.onPlan >= 5 },
  { key: 'month_of_meals', icon: 'heart', title: 'Month of meals', hint: 'Cook at least once a week, 4 weeks running', earned: (f) => f.bestStreak >= 4 },
  { key: 'workhorse', icon: 'chef', title: 'Kitchen workhorse', hint: 'Cook 50 times', earned: (f) => f.cooks >= 50 },
  { key: 'season', icon: 'trophy', title: 'A whole season', hint: 'Cook every week for 12 weeks', earned: (f) => f.bestStreak >= 12 },
]

/* ── Buddies ────────────────────────────────────────────────────────────── */

/**
 * The little guys. One is chosen per person and kept server-side, so it
 * follows them from the phone to the laptop. The drawings live in
 * src/components/sm/Buddy.tsx; this list is what the server validates against.
 */
export const BUDDIES = [
  { key: 'tomate', name: 'Tomatito', blurb: 'Cheerful, a little saucy, always ready to roll.' },
  { key: 'aguacate', name: 'Avo', blurb: 'Laid back. Believes every dish is better with a little green.' },
  { key: 'bao', name: 'Bao', blurb: 'Soft, patient, and very proud of you for trying.' },
  { key: 'flan', name: 'Flancito', blurb: 'Wobbly with excitement. Loves a sweet finish.' },
  { key: 'platano', name: 'Plátano', blurb: 'From the island. Thinks everything goes with tostones.' },
  { key: 'picante', name: 'Spicy', blurb: 'Too cool to take the shades off. Turns up the heat on every recipe.' },
] as const

export type BuddyKey = typeof BUDDIES[number]['key']

export function isBuddy(key: unknown): key is BuddyKey {
  return typeof key === 'string' && BUDDIES.some((b) => b.key === key)
}

/** Buddies grow a chef's hat as you level: none, a small toque, then a tall one. */
export function hatFor(levelIndex: number): 0 | 1 | 2 {
  return levelIndex >= 4 ? 2 : levelIndex >= 2 ? 1 : 0
}

/* ── The summary both halves agree on ───────────────────────────────────── */

export interface KitchenSummary {
  xp: number
  level: Level
  streak: Streak
  badges: { key: string; earned: boolean }[]
  facts: KitchenFacts
}

export function summarise(f: KitchenFacts, today: string): KitchenSummary {
  const streak = streakFor(f.cookedOn, today)
  const xp = xpFor(f)
  const bf: BadgeFacts = { ...f, bestStreak: streak.best }
  return {
    xp, level: levelFor(xp), streak,
    badges: BADGES.map((b) => ({ key: b.key, earned: b.earned(bf) })),
    facts: f,
  }
}

/** What changed between two summaries — what a celebration should say. */
export function momentsBetween(before: KitchenSummary, after: KitchenSummary): { xp: number; levelUp: Level | null; newBadges: string[] } {
  const had = new Set(before.badges.filter((b) => b.earned).map((b) => b.key))
  return {
    xp: after.xp - before.xp,
    levelUp: after.level.index > before.level.index ? after.level : null,
    newBadges: after.badges.filter((b) => b.earned && !had.has(b.key)).map((b) => b.key),
  }
}
